// InfosTab — Onglet « Infos » de la fiche dossier TEF (spec docs/upload/mockup-infos.html)
// Largeur d'abord : toute la structure (7 blocs) branchée sur les vraies données.
// Le récap header n'est PAS répété ici. Source client = metier_clients (embed dossier.client).
// IA « Remplir depuis documents » = STUB (moteur d'extraction = chantier séparé, signalé).
import RepresentantDossier from "../../../components/metier/RepresentantDossier";
import { useState } from "react";
import { Sparkles, CheckCircle2, XCircle, Lock, Edit3, Save, X, Loader2 } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { BlockCard } from "../../../components/BlockCard";
import { API, getAuthHeaders } from "./helpers";
// 🔑 N°705 — LES BLOCS MÉTIER SONT GATÉS PAR CAPABILITY, ⛔ JAMAIS PAR SLUG.
import { hasDossierAmo, hasDevisInstallateur } from "../../../utils/tenantProfile";
import { deriveDevisMode, DEVIS_MODES } from "../../../utils/devisMode";
import { patchClient, patchLogement } from "./dossierEditHandlers";
import { EditText, EditSelect } from "./champsEdition";
import { formatClientFullName, formatAdresse, getClientAge, formatGirOrNonRequis } from "../../../utils/clientFormat";
import { useConfigList, configLabel } from "../../../hooks/useConfigList";
import { CONTACT_RELATION_OPTIONS } from "../../../utils/contactSecondaire";
import { toast } from "sonner";
import { AdressesBlock } from "./AdressesBlock";

// ─── primitives ───
// Block local SUPPRIMÉ → primitive partagée <BlockCard> (chantier style 1/3).
function CompletudeBadge({ missing, label }) {
  if (missing <= 0) return <Badge className="text-[10px] border-0" style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }} data-testid="badge-complet">Complet</Badge>;
  return <Badge className="text-[10px] border-0" style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}>{label || `${missing} à remplir`}</Badge>;
}
function F({ label, value, note }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide" style={{ color: "var(--oh-text-muted)" }}>{label}</div>
      <div className="text-sm" style={{ color: value ? "var(--oh-text)" : "var(--oh-text-muted)" }}>
        {value || "—"}{note && <span className="text-[10px] ml-1" style={{ color: "var(--oh-stat-amber-text)" }}>{note}</span>}
      </div>
    </div>
  );
}
// 🔴 N°744 — L'AUTORITÉ DU TYPE DE CLIENT EST LE TENANT : la liste vient de
// `metier_config_listes{type:"client_types"}` via `useConfigList`, comme les civilités.
// ⚠️ MESURÉ le 2026-09-27 : cette liste existe (tef seulement) avec 3 items
//    — `particulier`, `pro`, `entreprise` — et **AUCUN code ne la lisait** ; « Syndic »
//    n'y était pas. C'est ce que JJ a vu manquer à l'écran (N°744).
// ⚠️ ET LE REPLI DOIT COUVRIR CE QUE LES DONNÉES PORTENT, sinon un client existant
//    s'affiche avec un select VIDE : mesuré en base, `professionnel` = 151 clients PROD,
//    `particulier` = 91, et `pro` n'est porté par AUCUN client alors qu'il est dans la liste.
//    ⇒ Le repli liste les valeurs OBSERVÉES + celles que `core/client_type.py::B2B_TYPE_VALUES`
//    reconnaît : ⛔ inventer une valeur hors de cet ensemble ferait rater `is_b2b()` et
//    produirait un faux B2C.
// 🔴 RÈGLE N°745 — ⛔ « SYNDIC » N'EST PAS DANS CE REPLI, ET ⛔ NE DOIT PAS Y ÊTRE.
//    Une valeur NOUVELLE ajoutée ici recréerait le doublon d'autorité qu'on vient de fermer :
//    elle s'afficherait sans exister dans la liste du tenant, donc sans être éditable ni
//    désactivable par lui. « Syndic / copropriété » entre par la liste du TENANT
//    (migration `061_client_types_syndic_2026_09_27`), ⛔ pas par le code.
// 🔑 CE REPLI N'EST QU'UN FILET DE NON-RÉGRESSION : il ne contient QUE les valeurs déjà
//    PORTÉES PAR LES DONNÉES (mesuré 2026-09-27 : `professionnel` 151 clients PROD,
//    `particulier` 91, `entreprise` 1 preprod). Sans lui, un client existant s'afficherait
//    avec un select VIDE si la liste du tenant n'est pas seedée.
const TYPE_CLIENT_REPLI = [
  { value: "particulier",   label: "Particulier" },
  { value: "professionnel", label: "Professionnel" },
  { value: "entreprise",    label: "Entreprise" },
];

// #546 — `ok` est TRI-ÉTAT : `true` satisfait · `false` non satisfait ·
// `null`/`undefined` INDÉTERMINÉ. Une case vide disait « non » là où la donnée
// n'avait jamais été saisie : c'est ce que ce lot corrige.
function Crit({ ok, children }) {
  const indetermine = ok === null || ok === undefined;
  return (
    <div className="flex items-center gap-2 py-0.5 text-xs" style={{ color: "var(--oh-text-secondary)" }}>
      {ok === true
        ? <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 text-[var(--oh-success-text)]" />
        : indetermine
        ? <span className="w-3.5 h-3.5 flex-shrink-0 rounded-[3px] flex items-center justify-center text-[9px] font-medium"
                style={{ border: "1px solid var(--oh-stat-amber-border)", color: "var(--oh-stat-amber-text)" }}
                title="Donnée jamais saisie — indéterminé">?</span>
        : <span className="w-3.5 h-3.5 flex-shrink-0 rounded-[3px]" style={{ border: "1px solid var(--oh-border)" }} />}
      <span>{children}</span>
      {indetermine && <span className="text-[10px]" style={{ color: "var(--oh-stat-amber-text)" }}>indéterminé</span>}
    </div>
  );
}

export function InfosTab({ dossier, dossierId, tenantId, onUpdate }) {
  // 🔑 N°705-3 — la MÊME capability que l'entrée de sous-menu correspondante.
  // ⛔ Absente ⇒ ⛔ pas de bloc AMO (défaut sûr, DEC-079).
  const amo = hasDossierAmo();
  const client = dossier?.client || {};
  // 🩹 N°838/844 — PANSEMENT pour la venue de Mario, ⛔ PAS une architecture.
  // Occupation, contact secondaire (« Lien » = proche aidant) et la note IA
  // RFR/CNI/catégorie ressources sont des objets d'AMO/éligibilité. On les cache
  // pour un tenant qui ne déclare NI `dossier_amo` (tef) NI
  // `devis_installateur_requis` (omv_france, climatpac) ⇒ tef/omv/climat voient
  // EXACTEMENT ce qu'ils voyaient ; seul mario_bat change.
  // ⏸️ Décision JJ N°844 : c'est la DERNIÈRE fois qu'on cache — même métier on
  // reprend, métier différent on CRÉE (audit des modules à venir).
  const blocAmo = amo || hasDevisInstallateur();
  const personneMorale = deriveDevisMode(client) === DEVIS_MODES.B2B;
  const verrouille = dossier?.verrouille;
  const headers = getAuthHeaders(tenantId, "application/json");

  // Listes config (zéro hard-code)
  const { items: occItems } = useConfigList("statuts_occupation");
  const { items: typeLogItems } = useConfigList("types_logement");
  const { items: sourceLeadItems } = useConfigList("source_lead_types");
  const { items: civiliteItems } = useConfigList("civilites");
  // 🔴 N°744 : l'autorité est le tenant. ⚠️ Repli si la liste n'est pas seedée — sinon le
  // select serait VIDE et le type de client deviendrait insaisissable.
  const { items: clientTypeItems } = useConfigList("client_types");
  const typeClientOptions = clientTypeItems.length
    ? clientTypeItems.map(o => ({ value: o.id, label: o.label }))
    : TYPE_CLIENT_REPLI;

  // ── données client (source = metier_clients, embed dossier.client) ──
  const nom = formatClientFullName(client) || dossier?.client_nom || "";
  const tel = client.telephone || dossier?.telephone || "";
  const email = client.email || "";
  const occupation = configLabel(occItems, client.statut_occupation, client.statut_occupation);
  const sourceLead = configLabel(sourceLeadItems, dossier?.source_lead || client.source_lead, dossier?.source_lead || client.source_lead);
  const c2 = {
    nom: client.contact_secondaire_nom, prenom: client.contact_secondaire_prenom,
    tel: client.contact_secondaire_telephone, lien: client.contact_secondaire_relation,
  };

  // ── logement ──
  const adresse = formatAdresse(dossier?.adresse_projet) || formatAdresse(client.adresse) || "";
  const typeLogementRaw = dossier?.workflow_data?.tef?.infos_logement?.type_logement || dossier?.infos_logement?.type_logement;
  const typeLogement = configLabel(typeLogItems, typeLogementRaw, typeLogementRaw);
  // #546 — champ CANONIQUE : le dossier (le logement), jamais le client.
  // `null` = INDÉTERMINÉ ; l'écran l'affiche déjà comme tel (l.303 : `== null` → "").
  const residencePrincipale =
    dossier?.residence_principale ?? dossier?.eligibilite_mpa?.residence_principale ?? null;

  // ── MPA / éligibilité ──
  const rfr = client.rfr ?? client.revenu_fiscal_reference ?? null;
  const nbFoyer = client.nb_personnes_foyer ?? client.nombre_personnes_foyer ?? null;
  const age = getClientAge(client);
  const gir = client.gir;
  const tauxHandicap = client.taux_handicap ?? null;
  const pch = client.beneficiaire_pch === true;
  const catRess = dossier?.categorie_ressources;
  const catRessLabel = (catRess && (catRess.label || catRess.categorie || catRess.niveau)) || (rfr == null ? "RFR manquant" : "—");
  const elig = dossier?.eligibilite_mpa || {};

  // 5 critères d'éligibilité bénéficiaire
  const critAge = (age != null && age >= 70) || (age != null && age >= 60 && age <= 69 && /^[1-6]$/.test(String(gir || "")));
  const critHandicap = (tauxHandicap != null && Number(tauxHandicap) > 50) || pch;
  const critOccupation = ["proprietaire", "propriétaire", "locataire", "locataire_prive"].includes(String(client.statut_occupation || "").toLowerCase());
  // #546 — tri-état : `null` remonte tel quel jusqu'à la checklist, qui affiche
  // « indéterminé » au lieu d'une case vide indiscernable d'un « non ».
  const critResidence = residencePrincipale == null ? null : residencePrincipale === true;
  const critRevenus = !!(catRess && (catRess.categorie || catRess.niveau || catRess.label)) && rfr != null;
  const verdict = elig.verdict || ((critAge || critHandicap) && critOccupation && critResidence && critRevenus ? "Éligible" : "Incomplet");
  const verdictColor = /éligible|eligible/i.test(verdict) && !/non|in/i.test(verdict) ? "green" : /non|refus/i.test(verdict) ? "red" : "amber";

  // complétude par bloc
  const clientMissing = [nom, tel, email, ...(blocAmo ? [client.statut_occupation] : [])].filter(v => !v).length;
  const logementMissing = [adresse, typeLogementRaw, residencePrincipale != null ? "x" : ""].filter(v => !v).length;

  // ── ANAH (Fernet, endpoint existant) ──
  const [editAnah, setEditAnah] = useState(false);
  const [savingAnah, setSavingAnah] = useState(false);
  const [anahOpen, setAnahOpen] = useState(false); // bloc ANAH replié par défaut (cohérence MPA)
  const [anahForm, setAnahForm] = useState({ anah_login: "", anah_password: "" });
  // « Modifier » est dans le header (summary) : on déplie le bloc à l'édition,
  // sinon le formulaire (dans le corps) resterait masqué par le repli.
  const startEditAnah = () => { setAnahForm({ anah_login: dossier?.anah_login || "", anah_password: "" }); setEditAnah(true); setAnahOpen(true); };
  const saveAnah = async () => {
    setSavingAnah(true);
    try {
      const body = { anah_login: anahForm.anah_login || null };
      if (anahForm.anah_password) body.anah_password = anahForm.anah_password;
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/anah-credentials`, {
        method: "PATCH", headers, credentials: "include", body: JSON.stringify(body),
      });
      if (res.ok) { toast.success("Compte ANAH mis à jour"); setEditAnah(false); onUpdate?.(); }
      else { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Erreur"); }
    } catch { toast.error("Erreur réseau"); }
    finally { setSavingAnah(false); }
  };

  // ── CC-DEV1-18 — édition Client / Logement (restaurée depuis AdminBlock,
  //    handlers extraits dans dossierEditHandlers, pattern = bloc Compte ANAH) ──
  const clientId = client.id || dossier?.client_id;
  const [editClient, setEditClient] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [clientForm, setClientForm] = useState({});
  const startEditClient = () => {
    setClientForm({
      civilite: client.civilite || "",
      nom: client.nom || dossier?.client_nom || "",
      prenom: client.prenom || "",
      telephone: client.telephone || dossier?.telephone || "",
      email: client.email || "",
      statut_occupation: client.statut_occupation || "",
      contact_secondaire_nom: client.contact_secondaire_nom || "",
      contact_secondaire_prenom: client.contact_secondaire_prenom || "",
      contact_secondaire_telephone: client.contact_secondaire_telephone || "",
      contact_secondaire_relation: client.contact_secondaire_relation || "",
    });
    setEditClient(true); setClientOpen(true);
  };
  const saveClient = async () => {
    setSavingClient(true);
    const r = await patchClient(clientId, clientForm, tenantId);
    if (r.ok) { toast.success("Client mis à jour"); setEditClient(false); onUpdate?.(); }
    else toast.error(r.error);
    setSavingClient(false);
  };

  const [editLogement, setEditLogement] = useState(false);
  const [logementOpen, setLogementOpen] = useState(false);
  const [savingLogement, setSavingLogement] = useState(false);
  const [logementForm, setLogementForm] = useState({});
  const startEditLogement = () => {
    const ap = dossier?.adresse_projet || {};
    const cadr = client.adresse || {};
    const tef = dossier?.workflow_data?.tef?.infos_logement || {};
    setLogementForm({
      rue: ap.rue || cadr.rue || "",
      code_postal: ap.code_postal || cadr.code_postal || "",
      ville: ap.ville || cadr.ville || "",
      type_logement: tef.type_logement || dossier?.infos_logement?.type_logement || "",
    });
    setEditLogement(true); setLogementOpen(true);
  };
  const saveLogement = async () => {
    setSavingLogement(true);
    const r = await patchLogement(dossier, logementForm, tenantId);
    if (r.ok) { toast.success("Logement mis à jour"); setEditLogement(false); onUpdate?.(); }
    else toast.error(r.error);
    setSavingLogement(false);
  };

  // ── IA « Remplir depuis documents » — STUB (moteur = chantier séparé) ──
  const handleFillFromDocs = () => {
    toast.info("Le pré-remplissage depuis les documents n'est pas encore disponible.");
  };

  // CC-DEV1-17 #1 — section « À compléter » (ex « Prochaines actions ») déplacée
  // vers l'onglet Demande MPA (composant ACompleterBlock, source unique de la logique).

  return (
    <div className="w-full" data-testid="infos-tab">
      {/* CC-DEV1-15 — largeur désormais portée par le cadre central commun de
          DossierDetail (max-w-[1100px] mx-auto). Plus de bridage 860px local.
          Espacement vertical = margin-bottom 10px de chaque BlockCard. */}
      {/* ── Toprow : note + Remplir depuis documents (IA stub) ── */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="text-[11px] italic" style={{ color: "var(--oh-text-muted)" }}>Informations du dossier</span>
        {/* CC-DEV1 — PAS de `text-[var(--oh-text)]` en className : en mode clair, `.light .text-[var(--oh-text)]`
            (index.css) le repeint en #18181b (noir) sur ce fond foncé → illisible. Le variant
            `default` fournit déjà `!text-[var(--oh-text)]` (classe `\!text-[var(--oh-text)]`, non matchée par cette règle). */}
        <Button size="sm" onClick={handleFillFromDocs} className="h-8 text-xs" style={{ background: "#534ab7" }} data-testid="btn-remplir-ia">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Remplir depuis documents
        </Button>
      </div>

      {/* ── 2. Client (CC-DEV1-18 — édition restaurée, pattern ANAH) ── */}
      {/* Split view (design JJ) : bloc ancré data-subnav-id pour le master-detail. */}
      <div data-subnav-id="beneficiaire">
      <BlockCard collapsible open={clientOpen} onToggle={(e) => setClientOpen(e.currentTarget.open)} testId="bloc-infos-client" title="Client"
        badge={<CompletudeBadge missing={clientMissing} label={`${clientMissing} à compléter`} />}
        action={!verrouille && !editClient && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" style={{ color: "var(--oh-stat-blue-text)" }} onClick={startEditClient} data-testid="btn-edit-client">
            <Edit3 className="w-3.5 h-3.5 mr-1" /> Modifier
          </Button>
        )}>
        {editClient ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <EditSelect label="Civilité" value={clientForm.civilite} onChange={(v) => setClientForm(f => ({ ...f, civilite: v }))} options={(civiliteItems.length ? civiliteItems.map(o => ({ value: o.id, label: o.label })) : [{ value: "M.", label: "M." }, { value: "Mme", label: "Mme" }])} testId="input-client-civilite" />
              <EditText label="Nom" value={clientForm.nom} onChange={(v) => setClientForm(f => ({ ...f, nom: v }))} testId="input-client-nom" />
              {/* SAV #644 — l'input Prénom manquait ICI, et seulement ici : `clientForm.prenom`
                  était déjà initialisé (l.165), `patchClient` l'envoyait déjà, et il est dans
                  la whitelist back (`core/database.allowed_fields`). Sans champ atteignable,
                  Stéphanie a saisi le prénom DANS `nom` (« KATTAR Sylvie ») — et tous les
                  générateurs lisent `civilite + prenom + nom`, CERFA 13460-03 compris. */}
              <EditText label="Prénom" value={clientForm.prenom} onChange={(v) => setClientForm(f => ({ ...f, prenom: v }))} testId="input-client-prenom" />
              <EditText label="Téléphone" type="tel" value={clientForm.telephone} onChange={(v) => setClientForm(f => ({ ...f, telephone: v }))} testId="input-client-tel" />
              <EditText label="Email" type="email" value={clientForm.email} onChange={(v) => setClientForm(f => ({ ...f, email: v }))} testId="input-client-email" />
              <EditSelect label="Type de client" value={clientForm.type_client} onChange={(v) => setClientForm(f => ({ ...f, type_client: v }))} options={typeClientOptions} testId="input-client-type" />
              {blocAmo && (
                <EditSelect label="Occupation" value={clientForm.statut_occupation} onChange={(v) => setClientForm(f => ({ ...f, statut_occupation: v }))} options={occItems.map(o => ({ value: o.id, label: o.label }))} testId="input-client-occup" />
              )}
            </div>
            {blocAmo && (<>
            <div className="text-[11px] uppercase tracking-wide mt-1 mb-1" style={{ color: "var(--oh-text-muted)" }}>Contact secondaire</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <EditText label="Nom" value={clientForm.contact_secondaire_nom} onChange={(v) => setClientForm(f => ({ ...f, contact_secondaire_nom: v }))} testId="input-contact2-nom" />
              <EditText label="Prénom" value={clientForm.contact_secondaire_prenom} onChange={(v) => setClientForm(f => ({ ...f, contact_secondaire_prenom: v }))} testId="input-contact2-prenom" />
              <EditText label="Téléphone" type="tel" value={clientForm.contact_secondaire_telephone} onChange={(v) => setClientForm(f => ({ ...f, contact_secondaire_telephone: v }))} testId="input-contact2-tel" />
              <EditSelect label="Lien" value={clientForm.contact_secondaire_relation} onChange={(v) => setClientForm(f => ({ ...f, contact_secondaire_relation: v }))} options={CONTACT_RELATION_OPTIONS} testId="input-contact2-relation" />
            </div>
            </>)}
            <div className="flex gap-1.5 pt-1">
              <Button size="sm" onClick={saveClient} disabled={savingClient} className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-[var(--oh-accent-contrast)]" data-testid="btn-save-client">
                {savingClient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</>}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-3 text-xs" onClick={() => setEditClient(false)}><X className="w-3.5 h-3.5 mr-1" /> Annuler</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <F label="Nom" value={nom} />
              <F label="Téléphone" value={tel} />
              <F label="Email" value={email} />
              {blocAmo && <F label="Occupation" value={occupation} />}
              <F label="Source du lead" value={sourceLead} />
              {/* 🩹 N°838 — une personne morale (syndic…) : ce qui l'IDENTIFIE. */}
              {!blocAmo && personneMorale && (<>
                <F label="Raison sociale" value={client.raison_sociale || client.nom} />
                <F label="SIREN" value={client.siren} />
                <F label="SIRET" value={client.siret} />
              </>)}
            </div>
            {/* T-185 — représentant repris à la conversion (rien si la donnée est absente) */}
            <RepresentantDossier representant={dossier?.representant} />
            {blocAmo && (<>
            <div className="text-[11px] uppercase tracking-wide mt-3 mb-1" style={{ color: "var(--oh-text-muted)" }}>Contact secondaire</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <F label="Nom" value={c2.nom} />
              <F label="Prénom" value={c2.prenom} />
              <F label="Téléphone" value={c2.tel} />
              <F label="Lien" value={c2.lien} />
            </div>
            </>)}
          </>
        )}
      </BlockCard>

      </div>

      {/* ── ADR-003 · ADRESSES du dossier (1..n). ⚠️ Le bloc Logement ci-dessous GARDE son
             adresse : c'est le miroir du temps 2, ⛔ pas un doublon oublié. ── */}
      <AdressesBlock dossierId={dossierId} tenantId={tenantId} verrouille={verrouille} onUpdate={onUpdate} />

      {/* ── 3. Logement (CC-DEV1-18 — édition restaurée : adresse/CP/ville/type) ── */}
      <div data-subnav-id="logement">
      <BlockCard collapsible open={logementOpen} onToggle={(e) => setLogementOpen(e.currentTarget.open)} testId="bloc-infos-logement" title="Logement"
        badge={<CompletudeBadge missing={logementMissing} />}
        action={!verrouille && !editLogement && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" style={{ color: "var(--oh-stat-blue-text)" }} onClick={startEditLogement} data-testid="btn-edit-logement">
            <Edit3 className="w-3.5 h-3.5 mr-1" /> Modifier
          </Button>
        )}>
        {editLogement ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="col-span-2 sm:col-span-3">
                <EditText label="Adresse (rue)" value={logementForm.rue} onChange={(v) => setLogementForm(f => ({ ...f, rue: v }))} testId="input-logement-rue" />
              </div>
              <EditText label="Code postal" value={logementForm.code_postal} onChange={(v) => setLogementForm(f => ({ ...f, code_postal: v }))} testId="input-logement-cp" />
              <EditText label="Ville" value={logementForm.ville} onChange={(v) => setLogementForm(f => ({ ...f, ville: v }))} testId="input-logement-ville" />
              <EditSelect label="Type" value={logementForm.type_logement} onChange={(v) => setLogementForm(f => ({ ...f, type_logement: v }))} options={typeLogItems.map(o => ({ value: o.id, label: o.label }))} testId="input-logement-type" />
            </div>
            {/* P1.2 — Résidence principale / Statut d'occupation / Indivision NON édités
                ici (occupation = champ Client ; résidence/indivision = critères MPA). */}
            <div className="flex gap-1.5 pt-1">
              <Button size="sm" onClick={saveLogement} disabled={savingLogement} className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-[var(--oh-accent-contrast)]" data-testid="btn-save-logement">
                {savingLogement ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</>}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-3 text-xs" onClick={() => setEditLogement(false)}><X className="w-3.5 h-3.5 mr-1" /> Annuler</Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <F label="Adresse" value={adresse} />
            <F label="Type" value={typeLogement} />
            {/* #546 — « Indéterminé » explicite : un tiret muet se lisait « Non ». */}
            <F label="Résidence principale"
               value={residencePrincipale == null ? "Indéterminé" : (residencePrincipale ? "Oui" : "Non")}
               note={residencePrincipale == null ? "jamais saisie" : undefined} />
            {blocAmo && <F label="Statut d'occupation" value={occupation} />}
            <F label="Indivision" value={client.indivision == null ? "" : (client.indivision ? "Oui" : "Non")} />
          </div>
        )}
      </BlockCard>

      </div>

      {/* ── 4. MaPrimeAdapt' ──
          🔴🔴 N°705-3 — CE BLOC ⛔ N'ÉTAIT GATÉ PAR RIEN. ⚠️ L'ENTRÉE de
          sous-menu `mpa`, elle, EST filtrée dans DossierDetail — mais le BLOC
          se rendait quand même dans le flux : c'est LE « MPA » que JJ voit
          chez Mario. 🔑 Un gate sur le MENU ⛔ n'est PAS un gate sur le
          CONTENU (leçon inverse du N°648). Même capability que l'entrée :
          `hasDossierAmo()` — mario_bat ⛔ ne la déclare PAS. */}
      {amo && (
      <div data-subnav-id="mpa">
      <BlockCard collapsible testId="bloc-infos-mpa" title="MaPrimeAdapt'" badge={rfr == null ? <Badge className="text-[10px] border-0" style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}>RFR manquant</Badge> : <CompletudeBadge missing={[rfr, nbFoyer, age].filter(v => v == null).length} />}>
        <div className="rounded-md p-2.5 mb-2 text-[11px] flex items-start gap-2" style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}>
          <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span><b>RFR auto (IA)</b> — extrait de l'avis d'imposition. Avis absent → télécharger l'avis (l'IA l'extrait). Pas de saisie manuelle.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <F label="RFR (auto IA depuis l'avis)" value={rfr != null ? `${Number(rfr).toLocaleString("fr-FR")} €` : ""} note={rfr == null ? "avis manquant" : null} />
          <F label="Personnes au foyer" value={nbFoyer != null ? String(nbFoyer) : ""} />
          <F label="Catégorie ressources (calculée)" value={catRessLabel} />
        </div>
        <div className="text-[11px] uppercase tracking-wide mt-3 mb-1" style={{ color: "var(--oh-text-muted)" }}>Âge &amp; GIR</div>
        <div className="rounded-md p-2.5 mb-2 text-[11px] flex items-start gap-2" style={{ background: "var(--oh-stat-amber-bg)", color: "var(--oh-stat-amber-text)" }}>
          <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <span><b>Âge auto (IA)</b> — calculé depuis la CNI. <b>&gt; 70 ans</b> → GIR non nécessaire ; <b>&lt; 70 ans</b> → GIR nécessaire.</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <F label="Âge (auto IA depuis la CNI)" value={age != null ? `${age} ans` : ""} note={age == null ? "CNI manquante" : null} />
          {/* GIR conditionné à l'âge : >70 non nécessaire / <70 nécessaire */}
          <F label="GIR" value={age != null && age >= 70 ? "Non nécessaire (≥70 ans)" : (formatGirOrNonRequis ? formatGirOrNonRequis(client) : (gir || "selon âge"))} />
        </div>
        <div className="text-[11px] uppercase tracking-wide mt-3 mb-1" style={{ color: "var(--oh-text-muted)" }}>Éligibilité bénéficiaire (validation du dossier)</div>
        <div data-testid="mpa-criteres">
          <Crit ok={critAge}>Âge 70+ (sans condition GIR) OU 60-69 ans avec GIR 1-6</Crit>
          <Crit ok={critHandicap}>OU handicap (taux incapacité &gt;50% ou éligible PCH)</Crit>
          <Crit ok={critOccupation}>Propriétaire occupant ou locataire parc privé</Crit>
          <Crit ok={critResidence}>Résidence principale</Crit>
          <Crit ok={critRevenus}>Revenus modestes ou très modestes (barème ANAH)</Crit>
        </div>
        <div className="mt-2 text-sm" style={{ color: "var(--oh-text-secondary)" }}>
          Verdict éligibilité MPA : <Badge className="text-[10px] border-0" data-testid="mpa-verdict" style={{ background: `var(--oh-stat-${verdictColor}-bg)`, color: `var(--oh-stat-${verdictColor}-text)` }}>{verdict}</Badge>
        </div>
      </BlockCard>

      </div>
      )}

      {/* ── 5. Compte ANAH bénéficiaire (confidentiel, Fernet) ──
          🔴 N°705-3 — MÊME CAS : l'entrée `compte-anah` est filtrée, le bloc
          ⛔ ne l'était PAS. Un compte ANAH est un objet d'AMO. */}
      {amo && (
      <div data-subnav-id="compte-anah">
      <BlockCard collapsible open={anahOpen} onToggle={(e) => setAnahOpen(e.currentTarget.open)} testId="bloc-infos-anah"
        title={<><Lock className="w-3.5 h-3.5" /> Compte ANAH bénéficiaire <Badge className="text-[10px] border-0" style={{ background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)" }}>Confidentiel</Badge></>}
        action={!verrouille && !editAnah && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" style={{ color: "var(--oh-stat-blue-text)" }} onClick={startEditAnah} data-testid="btn-edit-anah">
            <Edit3 className="w-3.5 h-3.5 mr-1" /> Modifier
          </Button>
        )}>
        {editAnah ? (
          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide mb-1" style={{ color: "var(--oh-text-muted)" }}>Identifiant ANAH</div>
                <input value={anahForm.anah_login} onChange={(e) => setAnahForm(f => ({ ...f, anah_login: e.target.value }))}
                  className="w-full rounded-md px-2 py-1.5 text-sm" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }} data-testid="input-anah-login" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide mb-1" style={{ color: "var(--oh-text-muted)" }}>Mot de passe ANAH</div>
                <input type="password" value={anahForm.anah_password} placeholder="laisser vide = inchangé"
                  onChange={(e) => setAnahForm(f => ({ ...f, anah_password: e.target.value }))}
                  className="w-full rounded-md px-2 py-1.5 text-sm" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }} data-testid="input-anah-password" />
              </div>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" onClick={saveAnah} disabled={savingAnah} className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="btn-save-anah">
                {savingAnah ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</>}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-3 text-xs" onClick={() => setEditAnah(false)}><X className="w-3.5 h-3.5 mr-1" /> Annuler</Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Identifiant ANAH" value={dossier?.anah_login ? "••••" + (dossier.anah_login.slice(-2)) : ""} />
            <F label="Mot de passe ANAH" value={dossier?.anah_password ? "••••••" : (dossier?.anah_login ? "(défini)" : "")} />
          </div>
        )}
        <p className="text-[10px] mt-2" style={{ color: "var(--oh-text-muted)" }}>
          Compte bénéficiaire sur monprojet.anah.gouv.fr. Mot de passe chiffré (Fernet) en base. Accès : Super Admin, Admin TEF, Installateur (sur ses dossiers).
        </p>
      </BlockCard>
      </div>
      )}

      {/* « Éligibilité rapide » FUSIONNÉ dans Logement (fin du doublon Résidence
          principale ; ces champs alimentent toujours le verdict éligibilité MPA). */}

      {/* CC-DEV1-17 #1 — « À compléter » déplacé vers l'onglet Demande MPA
          (ACompleterBlock). N'apparaît plus dans Infos. */}

      {/* Note IA (chantier séparé) */}
      {blocAmo && (
      <div className="rounded-md px-3 py-2 text-[11px] flex items-start gap-2" style={{ background: "var(--oh-stat-blue-bg)", color: "var(--oh-stat-blue-text)", borderLeft: "3px solid var(--oh-stat-blue-border)" }}>
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <span>La catégorie de ressources est calculée automatiquement.</span>
      </div>
      )}
    </div>
  );
}
